import { DateTime } from 'luxon';
import { WeekDay } from 'components/CalendarAvailabilityInput/types';
import { Calendar } from 'data/firestore/agent/calendar/types';

const WEEK_DAYS: WeekDay[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];

export const getWeekDayName = (day: Date, timeZone: string): WeekDay => {
    const weekday = DateTime.fromJSDate(day).setZone(timeZone).weekday % 7;
    return WEEK_DAYS[weekday];
};

const mergeAvailabilitySlots = (
    weekDay: WeekDay,
    slots: Array<{ startAt: number; duration: number }>,
): Calendar['availability'] => {
    if (slots.length === 0) {
        return [];
    }

    const sorted = [...slots].sort((left, right) => left.startAt - right.startAt);
    return sorted.reduce<Calendar['availability']>((merged, slot) => {
        const last = merged[merged.length - 1];
        const slotEnd = slot.startAt + slot.duration;
        if (!last || slot.startAt > last.startAt + last.duration) {
            merged.push({
                dayOfWeek: weekDay,
                startAt: slot.startAt,
                duration: slot.duration,
            });
            return merged;
        }
        last.duration = Math.max(last.startAt + last.duration, slotEnd) - last.startAt;
        return merged;
    }, []);
};

const isAvailableAt = (
    calendar: Calendar,
    weekDay: string,
    minute: number,
) => {
    const second = minute * 60;
    return (calendar.availability || []).some(({ dayOfWeek, startAt, duration }) => (
        dayOfWeek === weekDay
        && second >= startAt
        && second < startAt + duration
    ));
};

const getAvailabilityForWeekDay = ({
    calendars,
    day,
    timeZone,
}: {
    calendars: Calendar[];
    day: Date;
    timeZone: string;
}): Calendar['availability'] => {
    const weekDay = getWeekDayName(day, timeZone);
    const slots = calendars.flatMap((calendar) => (
        (calendar.availability || [])
            .filter(({ dayOfWeek }) => dayOfWeek === weekDay)
            .map(({ startAt, duration }) => ({ startAt, duration }))
    ));

    // Merge first. Day view passes one calendar; overlapping windows from
    // several calendars would break getUnavailableSlots.
    return mergeAvailabilitySlots(weekDay, slots);
};

export const getCalendarsForAvailableSlot = ({
    calendars,
    day,
    timeZone,
    minute,
}: {
    calendars: Calendar[];
    day: Date;
    timeZone: string;
    minute: number;
}) => {
    const weekDay = getWeekDayName(day, timeZone);
    return calendars.filter((calendar) => isAvailableAt(calendar, weekDay, minute));
};

export const getCalendarForAvailableSlot = ({
    calendars,
    day,
    timeZone,
    minute,
}: {
    calendars: Calendar[];
    day: Date;
    timeZone: string;
    minute: number;
}) => getCalendarsForAvailableSlot({
    calendars,
    day,
    timeZone,
    minute,
})[0];

export default getAvailabilityForWeekDay;
